import { Link } from "react-router-dom";
import { useEffect } from "react";
import { BookOpen, Calendar, ArrowRight, Clock, ChevronRight } from "lucide-react";
import { getSortedPosts } from "@/data/blogPosts";
import { IMAGES } from "@/lib/constants";

function formatDate(iso) {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function Blog() {
  const posts = getSortedPosts();

  useEffect(() => {
    document.title =
      "Cannabis Education Blog | MD Supply Wellness — Bethesda, MD";
    const desc =
      "Educational cannabis articles from MD Supply Wellness in Bethesda, MD — dosing, terpenes, Maryland delivery rules, medical vs. adult-use, and more.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://mdsupplywellness.com/blog");

    // JSON-LD Blog schema listing all posts
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-index-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "MD Supply Wellness Cannabis Education Blog",
      description: desc,
      url: "https://mdsupplywellness.com/blog",
      publisher: {
        "@type": "Organization",
        name: "MD Supply Wellness",
        url: "https://mdsupplywellness.com",
      },
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://mdsupplywellness.com/blog/${p.slug}`,
        datePublished: p.date,
        description: p.excerpt,
        keywords: (p.keywords || []).join(", "),
      })),
    });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("blog-index-jsonld");
      if (existing) existing.remove();
    };
  }, [posts]);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-index-page">
      {/* HERO */}
      <section className="py-14 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 flex items-center gap-1 text-xs text-white/50"
            data-testid="blog-index-breadcrumbs"
          >
            <Link to="/" className="hover:text-[#28C0D8]">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <Link to="/education" className="hover:text-[#28C0D8]">
              Education
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <span className="text-white/80">Blog</span>
          </nav>
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-8 animate-fade-up">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold inline-flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5" /> Education & Insights
              </p>
              <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                The <span className="text-[#28C0D8]">MD Supply</span> journal.
              </h1>
              <p className="mt-6 text-[17px] text-[#94A3B8] max-w-2xl">
                Plain-English guides on Maryland cannabis — dosing, terpenes,
                delivery rules, and how to shop a dispensary menu with
                confidence. Written by our team in Bethesda, Maryland.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 hidden md:block">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10">
                <img
                  src={IMAGES.macro2}
                  alt="Cannabis education"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="pb-8">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 rounded-3xl border border-white/10 bg-white/[0.02] hover:border-[#28C0D8]/40 transition-all p-6 md:p-8 lg:p-10"
            >
              <div className="relative aspect-[16/11] rounded-2xl overflow-hidden border border-white/10 order-1 lg:order-2 min-w-0">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="order-2 lg:order-1 flex flex-col justify-center min-w-0">
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(featured.date)}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {featured.readingTime} read
                  </span>
                </div>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white group-hover:text-[#28C0D8] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-[15px] text-[#94A3B8]">
                  {featured.excerpt}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(featured.tags || []).map((t) => (
                    <span
                      key={t}
                      className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 text-white/70"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-[#28C0D8] text-sm font-medium">
                  Read the article <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* GRID */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#28C0D8]/40 transition-all overflow-hidden min-w-0"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-3 text-[11px] text-white/50">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" />
                      {formatDate(p.date)}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/30" />
                    <span>{p.readingTime}</span>
                  </div>
                  <h3 className="mt-3 font-display text-lg md:text-xl font-semibold tracking-tight text-white group-hover:text-[#28C0D8] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#94A3B8] line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-auto pt-5 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider text-white/60">
                      {(p.tags && p.tags[0]) || "Education"}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#28C0D8] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
